import {Component, Injectable} from '@angular/core';
import {Observable} from 'rxjs/Observable';
import { AngularFireDatabase } from 'angularfire2/database';
import * as firebase from 'firebase';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/take';
import { AuthService } from './auth.service';
import { Angulartics2 } from 'angulartics2';

var UserActions = {
  Vote: 1,
  Comment: 2,
  CloseQuibbl: 3
}

@Injectable()
export class QuibblService {

  constructor(public af:AngularFireDatabase,
              private auth: AuthService,
              private angulartics2: Angulartics2) { }

  public quibbls = {};
  public activity = {};
  public likes = {};
  public activitiesLikedByUser = [];
  public comments = {};

  public currentQuibblIdentifier;

  getCommentsForQuibblAndUser(quibbl: string, user: string) {
    let comments = this.af.list('comments/' + quibbl + '/comments', {
      query: {
        orderByChild: 'user',
        equalTo: user,
        limitToLast: 1
      }
    });
    return comments;
  }

  getCommentsForQuibbl(quibbl: string) {
    if(this.comments[quibbl]) {
      return this.comments[quibbl];
    }

    let comments = this.af.list('comments/' + quibbl + '/comments');
    this.comments[quibbl] = comments;
    return comments;
  }

  deleteComment(quibbl: string, comment: string) {
    this.af.object('comments/' + quibbl + '/comments/' + comment).remove();
  }

  postCommentToQuibbl(quibbl: string, user: string, message: string) {
    this.af.list('userActions/' + this.auth.userIdentifier).push({
      type: UserActions.Comment,
      data: {
        quibbl: quibbl,
        message: message
      }
    });
  }

  getLikesForActivity(user: string, activity: string) {
    if(this.likes[activity]) {
      return this.likes[activity];
    }

    let likes = this.af.list('likes/' + activity + '/likes');
    likes.subscribe((obj) => {
      let liked = false;
      for(let i=0; i<obj.length; i++) {
        if(obj[i].$key == this.auth.userIdentifier) {
          liked = true;
          break;
        }
      }
      this.activitiesLikedByUser[activity] = liked;
    });
    this.likes[activity] = likes;
    return likes;
  }

  userDidLikeActivity(activity: string) {
    if(!this.likes[activity]) {
      this.getLikesForActivity(this.auth.userIdentifier, activity);
    }
    return this.activitiesLikedByUser[activity];
  }

  postLongformToQuibbl(quibbl: string, longform: string) {
    this.af.object('quibbls/' + quibbl + '/readMore').set(longform);
  }

  likeActivity(user: string, activity: string) {
    this.af.object('likes/' + activity + '/likes/' + user).set(true);
    this.activitiesLikedByUser[activity] = true;
    this.angulartics2.eventTrack.next({ action: 'Like', properties: { category: activity, label: user }});
  }

  unlikeActivity(user: string, activity: string) {
    this.af.object('likes/' + activity + '/likes/' + user).remove();
    this.activitiesLikedByUser[activity] = false;
    this.angulartics2.eventTrack.next({ action: 'Unlike', properties: { category: activity, label: user }});
  }

  getNextQuibbl(identifier: string) {
    let nextQuery = { query: {} };

    nextQuery.query['orderByKey'] = true;
    nextQuery.query['limitToLast'] = 2;
    nextQuery.query['endAt'] = identifier;

    return this.af.list('/quibbls', nextQuery);
  }

  // Get a quibbl
  getQuibbl(identifier: string) {
    if(this.quibbls[identifier]) {
      return this.quibbls[identifier];
    }

    let quibbl = this.af.object('quibbls/'+identifier);
    this.quibbls[identifier] = quibbl;
    return quibbl;
  }

  // Get the current value of a user's vote in a quibbl
  getCurrentVoteForUser(quibbl: string, user: string) {
    let vote = this.af.object('votes/'+quibbl+'/votes/'+user);
    return vote;
  }

  // Get a quibbl's votes
  getQuibblVoteList(identifier: string) {
    let votes = this.af.list('votes/'+identifier+'/votes', {
      query: {
        orderByChild: 'timestamp'
      }
    });
    return votes;
  }

  // Clear user's notifications
  clearNotifications() {
    this.af.object('notifications/'+this.auth.userIdentifier).remove();
  }

  // Get user's notifications
  getNotifications() {
    return this.af.object('notifications/'+this.auth.userIdentifier);
  }

  markAnswerAndCloseQuibbl(quibbl: string, answer: number) {
    this.af.list('userActions/' + this.auth.userIdentifier).push({
      type: UserActions.CloseQuibbl,
      data: {
        quibbl: quibbl,
        answer: answer
      }
    });
  }

  // Pin/unpin quibbl as featured
  setQuibblPin(quibbl: string, pinned: boolean) {
    this.af.object('quibbls/' + quibbl + '/pinned').set(pinned);
  }

  // Vote on a quibbl
  voteOnQuibbl(quibbl: string, vote: number) {
    this.af.list('userActions/' + this.auth.userIdentifier).push({
      type: UserActions.Vote,
      data: {
        quibbl: quibbl,
        vote: vote
      }
    });
  }

  getActivity(category?:string, limit?: number) {
    if((!limit || limit == 0) && this.activity[category]) {
      return this.activity[category];
    }

    let query = { query: {} };

    if(category) {
      query.query['orderByChild'] = 'category';
      query.query['equalTo'] = category;
    }

    if(limit) {
      query.query['limitToLast'] = limit;
    }

    let activity = this.af.list('activity', query);
    this.activity[category] = activity;
    return activity;
  }

  getActivityForUser(user:string, limit?: number) {
    if((!limit || limit == 0) && this.activity[user]) {
      return this.activity[user];
    }

    let query = { query: {} };
    query.query['orderByChild'] = 'user';
    query.query['equalTo'] = user;

    if(limit) {
      query.query['limitToLast'] = limit;
    }

    let userObj = this.af.list('activity', query);
    this.activity[user] = userObj;
    return userObj;
  }

  getTodayQuibbls() {
    var d = new Date();
    d.setHours(0,0,0,0);

    let query = {
      query: {
        orderByChild: 'timestamp',
        startAt: +d
      }
    };

    return this.af.list('quibbls', query);
  }

  getChannelChildren(category: string) {
    return this.af.list('static/channels/' + category);
  }

  // Get quibbls, ordered chronologically, optionally limited by (category || open) and count
  getQuibbls(category?: string, limit?: number, onlyOpen?: boolean, onlyPinned?: boolean) {
    let query = { query: {} };

    if(category) {
      query.query['orderByChild'] = 'category';
      query.query['equalTo'] = category;
    }

    if(onlyOpen) {
      query.query['orderByChild'] = 'closed';
      query.query['equalTo'] = null;
    }

    if(onlyPinned) {
      query.query['orderByChild'] = 'pinned';
      query.query['equalTo'] = true;
    }

    if(limit && limit > -1) {
      query.query['limitToLast'] = limit;
    }

    return this.af.list('quibbls', query);
  }
}



// WEBPACK FOOTER //
// /Users/nicholasgerard/Documents/Quibbl/quibbl/src/app/quibbl.service.ts